'use client';

import React, { createContext, ReactNode, useCallback, useContext, useEffect, useReducer } from 'react';

import { useRouter } from 'next/navigation';

import useWebAlias from '@/helpers/hooks/useWebalias';
import { PetPro, SignupFlowState, SignupFormData } from '@/types/Signup';

const SIGNUP_STORAGE_KEY = '__signup_flow_state';
const SIGNUP_FLOW_FLAG_KEY = '__signup_flow_active';

// Action Types
type SignupAction =
  | { type: 'SET_FORM_DATA'; payload: Partial<SignupFormData> }
  | { type: 'SET_CORPORATE_SIGNUP'; payload: boolean }
  | { type: 'SET_CURRENT_WEBALIAS'; payload: string }
  | { type: 'SET_SELECTED_PETPRO'; payload: PetPro | null }
  | { type: 'SET_REFERRAL_SOURCE'; payload: string | null }
  | { type: 'SET_REFERRAL_EXPLANATION'; payload: string | null }
  | { type: 'SET_STEP'; payload: SignupFlowState['step'] }
  | { type: 'RESET_SIGNUP_FLOW' }
  | { type: 'INITIALIZE_SIGNUP_FLOW'; payload: { isCorporate: boolean; webAlias: string } }
  | { type: 'RESTORE_STATE'; payload: SignupFlowState };

// Initial State
const initialState: SignupFlowState = {
  formData: {
    firstName: '',
    lastName: '',
    mobile: '',
    zipCode: '',
    email: '',
    password: '',
    confirmPassword: '',
    emailNotifications: false,
    smsNotifications: false,
    profilePicture: '',
    thirdPartyToken: '',
    thirdPartyType: '',
  },
  isCorporateSignup: false,
  currentWebAlias: '',
  selectedPetPro: null,
  referralSource: null,
  referralExplanation: null,
  step: 'form',
};

// Helper functions for localStorage
const saveToLocalStorage = (state: SignupFlowState) => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(SIGNUP_STORAGE_KEY, JSON.stringify(state));
      localStorage.setItem(SIGNUP_FLOW_FLAG_KEY, 'true');
    } catch (error) {
      console.error('Failed to save signup state to localStorage:', error);
    }
  }
};

const loadFromLocalStorage = (): SignupFlowState | null => {
  if (typeof window !== 'undefined') {
    try {
      const stored = localStorage.getItem(SIGNUP_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored) as SignupFlowState;
      }
    } catch (error) {
      console.error('Failed to load signup state from localStorage:', error);
    }
  }
  return null;
};

const clearLocalStorage = () => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem(SIGNUP_STORAGE_KEY);
      localStorage.removeItem(SIGNUP_FLOW_FLAG_KEY);
    } catch (error) {
      console.error('Failed to clear signup state from localStorage:', error);
    }
  }
};

export const isSignupFlowActive = (): boolean => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(SIGNUP_FLOW_FLAG_KEY) === 'true';
  }
  return false;
};

// Reducer
function signupReducer(state: SignupFlowState, action: SignupAction): SignupFlowState {
  if (action.type === 'RESET_SIGNUP_FLOW') {
    clearLocalStorage();
    return initialState;
  }

  if (action.type === 'RESTORE_STATE') {
    return action.payload;
  }

  let newState: SignupFlowState;

  switch (action.type) {
    case 'SET_FORM_DATA':
      newState = {
        ...state,
        formData: { ...state.formData, ...action.payload },
      };
      break;
    case 'SET_CORPORATE_SIGNUP':
      newState = {
        ...state,
        isCorporateSignup: action.payload,
      };
      break;
    case 'SET_CURRENT_WEBALIAS':
      newState = {
        ...state,
        currentWebAlias: action.payload,
      };
      break;
    case 'SET_SELECTED_PETPRO':
      newState = {
        ...state,
        selectedPetPro: action.payload,
      };
      break;
    case 'SET_REFERRAL_SOURCE':
      newState = {
        ...state,
        referralSource: action.payload,
      };
      break;
    case 'SET_REFERRAL_EXPLANATION':
      newState = {
        ...state,
        referralExplanation: action.payload,
      };
      break;
    case 'SET_STEP':
      newState = {
        ...state,
        step: action.payload,
      };
      break;
    case 'INITIALIZE_SIGNUP_FLOW':
      newState = {
        ...state,
        isCorporateSignup: state.currentWebAlias ? state.isCorporateSignup : action.payload.isCorporate,
        currentWebAlias: state.currentWebAlias || action.payload.webAlias,
      };
      break;
    default:
      return state;
  }

  saveToLocalStorage(newState);
  return newState;
}

// Context
interface SignupContextType {
  state: SignupFlowState;
  setFormData: (data: Partial<SignupFormData>) => void;
  setCorporateSignup: (isCorporate: boolean) => void;
  setCurrentWebAlias: (webAlias: string) => void;
  setSelectedPetPro: (petPro: PetPro | null) => void;
  setReferralSource: (source: string | null) => void;
  setReferralExplanation: (explanation: string | null) => void;
  setStep: (step: SignupFlowState['step']) => void;
  initializeSignupFlow: (isCorporate: boolean, webAlias: string) => void;
  resetSignupFlow: () => void;
  navigateToFindPetPro: (returnUrl?: string) => void;
  navigateToSelectPetPro: () => void;
  navigateToHearAboutUs: () => void;
  navigateToComplete: (returnUrl?: string, skip2FA?: boolean) => void;
}

const SignupContext = createContext<SignupContextType | undefined>(undefined);

// Provider
interface SignupProviderProps {
  children: ReactNode;
}

export function SignupProvider({ children }: SignupProviderProps) {
  const [state, dispatch] = useReducer(signupReducer, initialState);
  const router = useRouter();
  const { webalias, generateURL } = useWebAlias();

  useEffect(() => {
    const storedState = loadFromLocalStorage();
    if (storedState) {
      dispatch({ type: 'RESTORE_STATE', payload: storedState });
    }
  }, []);

  const setFormData = useCallback((data: Partial<SignupFormData>) => {
    dispatch({ type: 'SET_FORM_DATA', payload: data });
  }, []);

  const setCorporateSignup = useCallback((isCorporate: boolean) => {
    dispatch({ type: 'SET_CORPORATE_SIGNUP', payload: isCorporate });
  }, []);

  const setCurrentWebAlias = useCallback((webAlias: string) => {
    dispatch({ type: 'SET_CURRENT_WEBALIAS', payload: webAlias });
  }, []);

  const setSelectedPetPro = useCallback((petPro: PetPro | null) => {
    dispatch({ type: 'SET_SELECTED_PETPRO', payload: petPro });
  }, []);

  const setReferralSource = useCallback((source: string | null) => {
    dispatch({ type: 'SET_REFERRAL_SOURCE', payload: source });
  }, []);

  const setReferralExplanation = useCallback((explanation: string | null) => {
    dispatch({ type: 'SET_REFERRAL_EXPLANATION', payload: explanation });
  }, []);

  const setStep = useCallback((step: SignupFlowState['step']) => {
    dispatch({ type: 'SET_STEP', payload: step });
  }, []);

  const initializeSignupFlow = useCallback((isCorporate: boolean, webAlias: string) => {
    dispatch({ type: 'INITIALIZE_SIGNUP_FLOW', payload: { isCorporate, webAlias } });
  }, []);

  const resetSignupFlow = useCallback(() => {
    dispatch({ type: 'RESET_SIGNUP_FLOW' });
  }, []);

  const navigateToFindPetPro = useCallback(
    (returnUrl?: string) => {
      dispatch({ type: 'SET_SELECTED_PETPRO', payload: null });
      setStep('findPetPro');
      const queryParam = returnUrl ? `?ReturnUrl=${encodeURIComponent(returnUrl)}` : '';
      const path = `/account/findapetpro${queryParam}`;
      const url = generateURL(path, webalias);
      router.push(url);
    },
    [setStep, router, generateURL, webalias]
  );

  const navigateToSelectPetPro = useCallback(() => {
    setStep('selectPetPro');
  }, [setStep]);

  const navigateToHearAboutUs = useCallback(() => {
    setStep('hearAboutUs');
  }, [setStep]);

  const navigateToComplete = useCallback(
    (returnUrl?: string, skip2FA?: boolean) => {
      setStep('complete');
      clearLocalStorage();

      if (!returnUrl) {
        const baseUrl = skip2FA ? '/' : '/?modal=enable2FA';
        const urlWithWebalias = generateURL(baseUrl, webalias);
        window.location.href = urlWithWebalias;
        return;
      }

      const decodedReturnUrl = decodeURIComponent(returnUrl);
      const finalUrl = skip2FA
        ? decodedReturnUrl
        : decodedReturnUrl.includes('?')
        ? `${decodedReturnUrl}&modal=enable2FA`
        : `${decodedReturnUrl}?modal=enable2FA`;

      window.location.href = finalUrl;
    },
    [setStep, generateURL, webalias]
  );

  const value: SignupContextType = {
    state,
    setFormData,
    setCorporateSignup,
    setCurrentWebAlias,
    setSelectedPetPro,
    setReferralSource,
    setReferralExplanation,
    setStep,
    initializeSignupFlow,
    resetSignupFlow,
    navigateToFindPetPro,
    navigateToSelectPetPro,
    navigateToHearAboutUs,
    navigateToComplete,
  };

  return <SignupContext.Provider value={value}>{children}</SignupContext.Provider>;
}

// Hook
export function useSignup() {
  const context = useContext(SignupContext);
  if (context === undefined) {
    throw new Error('useSignup must be used within a SignupProvider');
  }
  return context;
}
